import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";

import SessionLoadingScreen from "components/SessionLoadingScreen";
import { useLanguage } from "context/LanguageContext";
import { clearSsoSession, completeSsoLogin } from "authService";

function SsoEntry() {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [error, setError] = useState("");

  useEffect(() => {
    let isMounted = true;
    let redirectTimeout;

    const validateSsoToken = async () => {
      const params = new URLSearchParams(location.search);
      const ssoToken = params.get("ssoToken");

      if (!ssoToken) {
        navigate("/dashboard", { replace: true });
        return;
      }

      try {
        const backendUrl = process.env.REACT_APP_API_URL || process.env.REACT_APP_BACKEND_BASE_URL;
        if (!backendUrl) {
          throw new Error("No está configurada la URL del backend.");
        }

        const response = await fetch(`${backendUrl}/partners/panel-sso/validar`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ ssoToken }),
        });

        const rawData = await response.json().catch(() => null);
        const data = rawData?.data && typeof rawData.data === "object"
          ? { ...rawData, ...rawData.data }
          : rawData;

        if (!response.ok || !data?.valid) {
          throw new Error(data?.error || data?.message || "No se pudo iniciar sesión.");
        }

        await completeSsoLogin(data);

        window.dispatchEvent(new Event("payflex-sso-authenticated"));
        navigate("/dashboard", { replace: true });
      } catch (ssoError) {
        clearSsoSession();
        console.error("❌ [SSO] Error al validar acceso:", ssoError);

        if (!isMounted) return;

        setError(ssoError.message || t("session.errorTitle"));
        redirectTimeout = setTimeout(() => {
          navigate("/authentication/sign-in", { replace: true });
        }, 2500);
      }
    };

    validateSsoToken();

    return () => {
      isMounted = false;
      if (redirectTimeout) {
        clearTimeout(redirectTimeout);
      }
    };
  }, [location.search, navigate, t]);

  if (!error) {
    return <SessionLoadingScreen />;
  }

  return (
    <Box
      minHeight="100vh"
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      px={3}
      textAlign="center"
      sx={{
        background: "linear-gradient(170deg, #0d1626 0%, #0a1220 55%, #081020 100%)",
        color: "#fff",
      }}
    >
      <Typography variant="h5" fontWeight={600} color="error.light" mb={1}>
        {t("session.errorTitle")}
      </Typography>
      <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.72)", maxWidth: 360, mb: 1 }}>
        {error}
      </Typography>
      <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.45)" }}>
        {t("session.errorRedirect")}
      </Typography>
    </Box>
  );
}

export default SsoEntry;
